import { App, Switch } from 'antd'
import { useTranslation } from 'react-i18next'

import { useUpdateMcpPolicy } from './hooks'
import { canEnablePolicy } from './model'
import type { McpServicePolicy } from './types'

type Props = {
  workspaceId: number
  policy: McpServicePolicy
}

/** The admin's on/off for one service; turning it off revokes its connections, so it asks first. */
export const ServiceSwitch = ({ workspaceId, policy }: Props) => {
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const update = useUpdateMcpPolicy(workspaceId)

  const serviceName = t(
    `workspace.aiConnectors.services.${policy.service}.name`
  )
  // An enabled service stays switchable so an admin can still turn it off.
  const enableBlocked = !policy.is_enabled && !canEnablePolicy(policy)

  const patch = (is_enabled: boolean) =>
    update.mutateAsync({ service: policy.service, body: { is_enabled } })

  const handleToggle = (checked: boolean) => {
    if (checked) {
      void patch(true).catch(() => null)

      return
    }
    modal.confirm({
      title: t('workspace.aiConnectors.servicesTab.disableTitle', {
        service: serviceName
      }),
      content: t('workspace.aiConnectors.servicesTab.disableBody'),
      okText: t('workspace.aiConnectors.servicesTab.disable'),
      okButtonProps: { danger: true },
      onOk: () => patch(false).catch(() => null)
    })
  }

  return (
    <Switch
      size="small"
      checked={policy.is_enabled}
      disabled={enableBlocked}
      loading={update.isPending}
      onChange={handleToggle}
      aria-label={t('workspace.aiConnectors.servicesTab.enabledAria', {
        service: serviceName
      })}
    />
  )
}
